import visitasAgendaService from './visitasAgendaService';
import { DraftByDay } from '../pages/ventas/components/planDraftByDay';
import {
  candidatoAppliesInRange,
  eachIsoDayInRange,
  isoDayMatchesAgendaDias,
  pickDireccionLabel,
  visitRowKey,
} from '../pages/ventas/components/planFormHelpers';
import { loadDireccionesForVisitRow } from '../pages/ventas/components/planVisitaRowLoaders';
import { emptyVisitRow, VisitRowState } from '../pages/ventas/components/planVisitaFormUi';
import { VisitasAgendaCandidato, VisitasAgendaDias } from '../types/visitasAgenda';

const apiDiasToFrontend = (c: VisitasAgendaCandidato): VisitasAgendaDias => ({
  lun: c.lunes,
  mar: c.martes,
  mier: c.miercoles,
  jue: c.jueves,
  vie: c.viernes,
  sab: c.sabado,
  dom: c.domingo,
});

const enrichCandidatoRow = async (c: VisitasAgendaCandidato): Promise<VisitRowState> => {
  const codigo = (c.codigoDireccion ?? '').trim();
  const nombre = c.nombreDisplay?.trim() || `Cliente ${c.nroCliente}`;
  const row: VisitRowState = {
    ...emptyVisitRow(),
    nroCliente: c.nroCliente,
    clienteNombre: nombre,
    codigoDireccion: codigo,
    direccionLabel: codigo || '',
    fromAgenda: true,
  };

  const patch = await loadDireccionesForVisitRow(c.nroCliente);
  const match = patch.direcciones.find((d) => (d.codigoDireccion ?? '').trim() === codigo);
  if (match) {
    return {
      ...row,
      ...patch,
      codigoDireccion: match.codigoDireccion || codigo,
      direccionLabel: pickDireccionLabel(match),
      fromAgenda: true,
    };
  }
  if (codigo && !patch.codigoDireccion) {
    return {
      ...row,
      ...patch,
      codigoDireccion: codigo,
      direccionLabel: codigo,
      fromAgenda: true,
    };
  }
  return { ...row, ...patch, fromAgenda: true };
};

export const fetchAgendaCandidatos = async (nroVendedor: number): Promise<VisitasAgendaCandidato[]> =>
  visitasAgendaService.getCandidatosAgenda(nroVendedor);

export const buildDraftByDayFromAgendaCandidatos = async (
  candidatos: VisitasAgendaCandidato[],
  fechaDesde: string,
  fechaHasta: string,
): Promise<DraftByDay> => {
  const days = eachIsoDayInRange(fechaDesde, fechaHasta);
  const result: DraftByDay = {};
  days.forEach((day) => {
    result[day] = [];
  });

  const aplicables = candidatos.filter((c) =>
    candidatoAppliesInRange(apiDiasToFrontend(c), fechaDesde, fechaHasta),
  );

  for (const c of aplicables) {
    const row = await enrichCandidatoRow(c);
    const dias = apiDiasToFrontend(c);
    const candidatoKey = visitRowKey(c.nroCliente, c.codigoDireccion ?? undefined);

    for (const day of days) {
      if (!isoDayMatchesAgendaDias(day, dias)) continue;
      const exists = result[day].some(
        (existing) => visitRowKey(existing.nroCliente, existing.codigoDireccion) === candidatoKey,
      );
      if (!exists) {
        result[day].push({ ...row });
      }
    }
  }

  return result;
};
